import { useState, useEffect, useCallback } from 'react'
import { supabase, HuaweiOrder } from '../lib/supabase'
import { useFilterStore } from '../store/filterStore'
import { withGlobalLoading } from '../store/loadingStore'

interface UseSupabaseQueryOptions {
  limit?: number
  page?: number
  searchQuery?: string
  orderBy?: { column: string; ascending: boolean }
}

export function useSupabaseQuery(options: UseSupabaseQueryOptions = {}) {
  const [data, setData] = useState<HuaweiOrder[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filters = useFilterStore()

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      let query = supabase
        .from('Huawei_order')
        .select('*', { count: 'exact' })

      // =====================
      // FILTERS
      // =====================
      if (filters.TREG?.trim()) {
        query = query.eq('TREG', filters.TREG)
      }

      if (filters.Regional_Tsel?.trim()) {
        query = query.eq('Regional_Tsel', filters.Regional_Tsel)
      }

      if (filters.Tipe_Site?.trim()) {
        query = query.eq('Tipe_Site', filters.Tipe_Site)
      }

      if (filters.Frekuensi?.trim()) {
        query = query.eq('Frekuensi', filters.Frekuensi)
      }

      if (filters.Status_Redeploy?.trim()) {
        query = query.eq('Status_Redeploy', filters.Status_Redeploy)
      }

      if (filters.StatusMaterial?.trim()) {
        query = query.eq('StatusMaterial', filters.StatusMaterial)
      }

      // 🔥 FIX MITRA
      if (filters.Mitra?.trim()) {
        query = query.eq('Mitra', filters.Mitra)
      }

      // =====================
      // SEARCH
      // =====================
      if (options.searchQuery?.trim()) {
        query = query.or(
          `Site_ID_NE.ilike.%${options.searchQuery}%,Site_Name_NE.ilike.%${options.searchQuery}%`
        )
      }

      // =====================
      // ORDER
      // =====================
      query = query.order(
        options.orderBy?.column || 'Site_ID_NE',
        {
          ascending: options.orderBy?.ascending ?? true,
        }
      )

      // =====================
      // PAGINATION
      // =====================
      const limit = options.limit || 10
      const page = options.page || 0

      const from = page * limit
      const to = from + limit - 1

      query = query.range(from, to)

      // =====================
      // EXECUTE
      // =====================
      const { data: result, error, count } = await withGlobalLoading(
        'table-query',
        options.searchQuery?.trim()
          ? `Mencari "${options.searchQuery.trim()}"…`
          : 'Memuat data…',
        () => query,
      )

      if (error) throw error

      setData(result || [])
      setCount(count || 0)

    } catch (err: any) {
      console.error('Supabase query error:', err)
      setError(err.message || 'Failed to fetch data')
      setData([])
      setCount(0)
    } finally {
      setLoading(false)
    }
  }, [
    filters.TREG,
    filters.Regional_Tsel,
    filters.Tipe_Site,
    filters.Frekuensi,
    filters.Status_Redeploy,
    filters.StatusMaterial,
    filters.Mitra, // 🔥 WAJIB ADA INI
    options.searchQuery,
    options.orderBy?.column,
    options.orderBy?.ascending,
    options.limit,
    options.page,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    count,
    loading,
    error,
    refetch: fetchData,
  }
}
